import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, KeyRound, Monitor, Smartphone } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { SessionSummary } from '../lib/auth/auth-contract.js';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import { PinResetDialog } from '../components/auth/pin-reset-dialog.js';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  useConfirm,
  ListSkeleton,
  useToast,
} from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { useRevokeSession, useSessions, useSignOutEverywhere } from '../lib/auth/use-sessions.js';
import { formatDateTime, formatRelativeTime } from '../lib/format.js';

/**
 * Security: where this account is signed in, and the PIN reset an owner does on somebody
 * else's behalf.
 *
 * The device list matters more here than in most products. A shop phone lives on a counter, a
 * manager signs in on their own handset, a tailor borrows one — and when somebody leaves, the
 * owner needs to end their sessions without changing a password everybody shares. "Sign out
 * everywhere" is the blunt instrument for the day a phone is lost.
 */
export function SecurityPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const confirm = useConfirm();
  const auth = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const sessions = useSessions();
  const revoke = useRevokeSession();
  const signOutEverywhere = useSignOutEverywhere();

  /**
   * The PIN reset is opened by a deep link — `?staff=<id>&name=<name>` — so task 19's staff
   * list can send an owner straight here from a "Reset PIN" button without this screen having
   * to know anything about how staff are listed.
   */
  const staffId = params.get('staff');
  const staffName = params.get('name') ?? t('auth.thisTailor');
  const [resetting, setResetting] = useState(staffId !== null);

  const closeReset = (): void => {
    setResetting(false);
    params.delete('staff');
    params.delete('name');
    setParams(params, { replace: true });
  };

  const endSession = (session: SessionSummary): void => {
    void (async () => {
      const confirmed = await confirm({
        title: t('security.endSessionTitle'),
        description: session.current
          ? t('security.endCurrentSessionDescription')
          : t('security.endSessionDescription'),
        confirmLabel: t('security.endSession'),
        destructive: true,
      });
      if (!confirmed) return;

      await revoke.mutateAsync(session.id);
      if (session.current) {
        // Revoking your own session cleared the cookies server-side; the local state has to
        // follow or the app would sit there making requests as nobody.
        await auth.signOut();
        void navigate('/login', { replace: true });
        return;
      }
      toast({ title: t('security.sessionEnded'), variant: 'success' });
    })();
  };

  const endEverything = (): void => {
    void (async () => {
      const confirmed = await confirm({
        title: t('security.signOutEverywhereTitle'),
        description: t('security.signOutEverywhereDescription'),
        confirmLabel: t('security.signOutEverywhere'),
        destructive: true,
      });
      if (!confirmed) return;

      await signOutEverywhere.mutateAsync();
      await auth.signOut();
      void navigate('/login', { replace: true });
    })();
  };

  return (
    <>
      <PageHeader title={t('security.title')} description={t('security.description')} />

      <div className="flex flex-col gap-6">
        <Link
          to="/settings"
          className="inline-flex items-center gap-1 self-start text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft aria-hidden="true" className="size-4" />
          {t('security.back')}
        </Link>

        <Card>
          <CardHeader>
            <CardTitle>{t('security.devicesTitle')}</CardTitle>
            <CardDescription>{t('security.devicesDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {sessions.isPending ? (
              <ListSkeleton rows={2} />
            ) : (sessions.data?.length ?? 0) === 0 ? (
              <EmptyState
                icon={Monitor}
                title={t('security.noDevices')}
                description={t('security.noDevicesHint')}
              />
            ) : (
              <ul className="flex flex-col gap-2">
                {sessions.data?.map((session) => (
                  <li
                    key={session.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                        <Smartphone aria-hidden="true" className="size-5" />
                      </span>
                      <div className="min-w-0">
                        <p className="flex items-center gap-2 font-medium">
                          {describeDevice(session.userAgent) ?? t('security.unknownDevice')}
                          {session.current ? (
                            <Badge variant="success">{t('security.thisDevice')}</Badge>
                          ) : null}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {t('security.lastUsed', {
                            when: formatRelativeTime(session.lastUsedAt),
                          })}
                          {session.ip ? ` · ${session.ip}` : ''}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {t('security.signedInAt', { when: formatDateTime(session.createdAt) })}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={revoke.isPending}
                      onClick={() => endSession(session)}
                    >
                      {session.current ? t('security.endThisSession') : t('security.endSession')}
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            <div>
              <Button
                variant="outline"
                disabled={signOutEverywhere.isPending}
                onClick={endEverything}
              >
                {t('security.signOutEverywhere')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('security.pinTitle')}</CardTitle>
            <CardDescription>{t('security.pinDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {staffId ? (
              <Button onClick={() => setResetting(true)}>
                <KeyRound aria-hidden="true" />
                {t('security.resetPinFor', { name: staffName })}
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">{t('security.pinFromStaffList')}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {resetting && staffId ? (
        <PinResetDialog
          staff={{ userId: staffId, name: staffName }}
          onOpenChange={(open) => !open && closeReset()}
          onDone={() => {
            toast({ title: t('security.pinReset', { name: staffName }), variant: 'success' });
          }}
        />
      ) : null}
    </>
  );
}

/**
 * A user-agent string, reduced to the two words a person recognises. Deliberately crude: this
 * is "is that my phone?", not device fingerprinting, and a wrong guess costs nothing.
 */
function describeDevice(userAgent: string | null): string | null {
  if (!userAgent) return null;
  const platform = /android/i.test(userAgent)
    ? 'Android'
    : /iphone|ipad|ios/i.test(userAgent)
      ? 'iPhone'
      : /windows/i.test(userAgent)
        ? 'Windows'
        : /mac os/i.test(userAgent)
          ? 'Mac'
          : /linux/i.test(userAgent)
            ? 'Linux'
            : null;
  const browser = /edg\//i.test(userAgent)
    ? 'Edge'
    : /chrome|crios/i.test(userAgent)
      ? 'Chrome'
      : /firefox|fxios/i.test(userAgent)
        ? 'Firefox'
        : /safari/i.test(userAgent)
          ? 'Safari'
          : null;
  if (!platform && !browser) return null;
  return [platform, browser].filter(Boolean).join(' · ');
}

export default SecurityPage;
